import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { Switch } from '@components/Switch';
import importService from '@services/importService';
import {
  IMPORT_RULE_APPLIES_TO_LABELS,
  MOVEMENT_LABELS,
  type Category,
  type ImportRule,
  type ImportRuleAppliesTo,
  type ImportRuleInput,
  type Movement,
} from '@/types/finance';
import { CategorySelect, Required, labelCls } from './financeBits';

interface Props {
  rule?: ImportRule | null;
  categories: Category[];
  /** Criação a partir de uma linha da prévia: padrão, categoria e movimento pré-preenchidos. */
  prefill?: Partial<ImportRuleInput>;
  onClose: () => void;
  onSaved: (rule: ImportRule) => void;
  onError: (err: unknown) => void;
}

const APPLIES: ImportRuleAppliesTo[] = ['both', 'account', 'card'];

/** Regra de categoria na importação (POST/PUT /import-rules): "descrição contém o padrão → sugere a categoria". */
export const ImportRuleFormModal: React.FC<Props> = ({ rule, categories, prefill, onClose, onSaved, onError }) => {
  const isEdit = !!rule;
  const [saving, setSaving] = useState(false);
  const [pattern, setPattern] = useState(rule?.pattern ?? prefill?.pattern ?? '');
  const [categoryId, setCategoryId] = useState<number | ''>(rule?.category_id ?? prefill?.category_id ?? '');
  const [appliesTo, setAppliesTo] = useState<ImportRuleAppliesTo>(rule?.applies_to ?? prefill?.applies_to ?? 'both');
  const [movement, setMovement] = useState<Movement | ''>(rule?.movement ?? prefill?.movement ?? '');
  const [priority, setPriority] = useState(rule?.priority ?? prefill?.priority ?? 100);
  const [active, setActive] = useState(rule?.is_active ?? true);

  const canSubmit = !saving && !!pattern.trim() && categoryId !== '';

  const save = async () => {
    if (!canSubmit) return;
    const payload: ImportRuleInput = {
      pattern: pattern.trim(),
      category_id: Number(categoryId),
      applies_to: appliesTo,
      movement: movement || null,
      priority,
      is_active: active,
    };
    setSaving(true);
    try {
      onSaved(rule ? await importService.updateRule(rule.id, payload) : await importService.createRule(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Wand2 size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar regra' : 'Nova regra de importação'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3">
        <p className="text-small">Quando a descrição da linha importada <strong>contém</strong> o padrão (sem distinguir maiúsculas), a importação sugere a categoria.</p>
        <div>
          <label className={labelCls}>
            Padrão (trecho da descrição)
            <Required />
          </label>
          <input className="input-field" maxLength={200} value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Ex.: UBER, IFOOD, PAG*" autoFocus />
        </div>
        <div>
          <label className={labelCls}>
            Categoria
            <Required />
          </label>
          <CategorySelect categories={categories} movement={movement || undefined} value={categoryId} onChange={setCategoryId} emptyLabel="Selecione…" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Aplica-se a</label>
            <select className="select-field" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value as ImportRuleAppliesTo)}>
              {APPLIES.map((a) => (
                <option key={a} value={a}>
                  {IMPORT_RULE_APPLIES_TO_LABELS[a]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Movimento</label>
            <select
              className="select-field"
              value={movement}
              onChange={(e) => {
                setMovement(e.target.value as Movement | '');
                setCategoryId('');
              }}
            >
              <option value="">Qualquer</option>
              <option value="expense">{MOVEMENT_LABELS.expense}</option>
              <option value="income">{MOVEMENT_LABELS.income}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Prioridade</label>
            <input type="number" className="input-field" min={1} max={9999} value={priority} onChange={(e) => setPriority(Math.max(1, Math.trunc(Number(e.target.value) || 100)))} />
            <p className="text-small mt-1">Menor vence; empate: padrão mais longo.</p>
          </div>
        </div>
        <Switch checked={active} onChange={setActive} label="Regra ativa" />
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Criar regra'}
        </Button>
      </div>
    </Modal>
  );
};
